import "react-datepicker/dist/react-datepicker.css";

import type { RefObject } from "react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Dropdown } from "react-bootstrap";
import ReactDatePicker from "react-datepicker";

import { CalendarIcon } from "../../icons";

interface IProps {
  queryValue: string;
  handleChange: (value: string, needUpdate: boolean) => void;
}

interface ICustomDate {
  fromDate?: number | null;
  toDate?: number | null;
}

interface IFooterProps {
  onSaveClick: () => void;
  customDate: ICustomDate | null;
}

const Footer: React.FC<IFooterProps> = memo(({ onSaveClick, customDate }) => {
  const handleSave = () => {
    onSaveClick();
  };

  return (
    <div className="d-flex justify-content-end date-controls pt-6">
      <Dropdown.Item as="div">
        <Button className="mx-2" size="sm" variant="secondary">
          Cancel
        </Button>
      </Dropdown.Item>
      <Dropdown.Item as="div">
        <Button
          size="sm"
          variant="primary"
          onClick={handleSave}
          disabled={!customDate?.fromDate || !customDate?.toDate}
        >
          Save
        </Button>
      </Dropdown.Item>
    </div>
  );
});

const DatePicker: React.FC<IProps> = memo(({ queryValue, handleChange }) => {
  const maxCustomDate = useMemo(() => new Date(), []);
  const [customDate, setCustomDate] = useState<ICustomDate | null>(null);
  const [openCalendarStart, setOpenCalendarStart] = useState(false);
  const [openCalendarEnd, setOpenCalendarEnd] = useState(false);
  const startPickerRef = useRef<ReactDatePicker>(null);
  const endPickerRef = useRef<ReactDatePicker>(null);
  const onSaveClick = () => {
    if (customDate?.fromDate && customDate?.toDate) {
      handleChange(
        `custom,${new Date(customDate.fromDate).getTime()},${new Date(
          customDate.toDate,
        ).getTime()}`,
        true,
      );
    }
  };

  const onStartDateChange = (date: Date) => {
    setCustomDate(prevDate => {
      const updatedDate = { ...(prevDate || {}) };
      if (prevDate?.toDate && date.getTime() > prevDate.toDate) {
        updatedDate.toDate = new Date(date).getTime();
      }
      updatedDate.fromDate = date ? date.getTime() : null;
      return updatedDate;
    });
  };

  const onEndDateChange = (date: Date) => {
    setCustomDate(prevDate => {
      const updatedDate = { ...(prevDate || {}) };
      updatedDate.toDate = date ? new Date(date).getTime() : null;
      return updatedDate;
    });
  };

  const presetDate = useCallback(() => {
    let parsedQueryValue: string[] = [];
    if (queryValue) {
      parsedQueryValue = queryValue.split(",");
    }
    if (parsedQueryValue.length === 3 && parsedQueryValue[0] === "custom") {
      setCustomDate({
        fromDate: Number(parsedQueryValue[1]),
        toDate: Number(parsedQueryValue[2]),
      });
    } else {
      setCustomDate(null);
    }
  }, [queryValue]);

  const closeBothCalendars = () => {
    setOpenCalendarStart(false);
    setOpenCalendarEnd(false);
  };

  useEffect(() => {
    presetDate();
  }, [presetDate]);

  const handlePopoverTriggerClick = () => {
    presetDate();
    closeBothCalendars();
  };

  const handleIconClick = (ref: RefObject<ReactDatePicker>) => {
    setTimeout(() => {
      ref.current?.setFocus();
    });
  };

  return (
    <Dropdown placement="bottom-end">
      <Dropdown.Toggle
        variant="outline-light"
        className={`date-filter-item no-arrow ${
          queryValue?.includes("custom") ? "active" : ""
        }`}
      >
        <label onClick={handlePopoverTriggerClick}>
          <CalendarIcon />
          Custom
        </label>
      </Dropdown.Toggle>
      <Dropdown.Menu align="end" className="date-picker">
        <div className="px-3 my-0 d-flex flex-column">
          <div className="d-flex gap-3">
            <div className="d-flex flex-column form-group">
              <label htmlFor="startDate" className="form-label">
                Start date
              </label>
              <ReactDatePicker
                ref={startPickerRef}
                onSelect={() => setOpenCalendarStart(false)}
                onFocus={() => {
                  setOpenCalendarStart(true);
                  setOpenCalendarEnd(false);
                }}
                onBlur={() => setOpenCalendarStart(false)}
                onInputClick={() => setOpenCalendarStart(true)}
                open={openCalendarStart}
                className="form-control date-picker__input"
                placeholderText="mm/dd/yyyy"
                selected={
                  customDate?.fromDate
                    ? new Date(customDate.fromDate)
                    : undefined
                }
                onChange={onStartDateChange}
                maxDate={maxCustomDate}
                showIcon
                icon={
                  <span
                    data-testid="calendar-icon"
                    className="date-picker-icon"
                    onMouseDown={() => handleIconClick(startPickerRef)}
                  >
                    <CalendarIcon />
                  </span>
                }
              />
            </div>
            <div className="d-flex flex-column form-group">
              <label htmlFor="endDate" className="form-label">
                End date
              </label>
              <ReactDatePicker
                ref={endPickerRef}
                onSelect={() => setOpenCalendarEnd(false)}
                onFocus={() => {
                  setOpenCalendarEnd(true);
                  setOpenCalendarStart(false);
                }}
                onBlur={() => setOpenCalendarEnd(false)}
                onInputClick={() => setOpenCalendarEnd(true)}
                open={openCalendarEnd}
                className="form-control date-picker__input"
                placeholderText="mm/dd/yyyy"
                selected={
                  customDate?.toDate ? new Date(customDate.toDate) : undefined
                }
                onChange={onEndDateChange}
                minDate={
                  customDate?.fromDate
                    ? new Date(customDate.fromDate)
                    : undefined
                }
                maxDate={maxCustomDate}
                showIcon
                icon={
                  <span
                    data-testid="calendar-icon"
                    className="date-picker-icon"
                    onMouseDown={() => handleIconClick(endPickerRef)}
                  >
                    <CalendarIcon />
                  </span>
                }
              />
            </div>
          </div>
          <Footer onSaveClick={onSaveClick} customDate={customDate} />
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
});

export { DatePicker };
